import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Notice } from '@/components/ui/notice';
import { Spinner } from '@/components/ui/spinner';
import { GeneratedLanguageNotice } from '@/lib/i18n/generated-language-notice';
import { loadErrorText } from '@/lib/api-error';
import { useDraftReviewContext } from '../hooks/draft-review-context';

type ExplainTarget =
  | { kind: 'item'; fieldKey: string; rowId: string | null }
  | { kind: 'section'; sectionId: number; title: string };

interface ExplainPanelProps {
  target: ExplainTarget;
  'data-testid': string;
}

/**
 * "Explain" toggle for one goal/service/accommodation row (or field). The
 * underlying revision-wide explanation set is fetched once, lazily, the first
 * time ANY card asks for it (`useDraftExplanations`); this card only shows its
 * own loading/error state once it has asked.
 */
export function ExplainPanel({ target, 'data-testid': testId }: ExplainPanelProps) {
  const { t } = useTranslation('shared-drafts');
  const ctx = useDraftReviewContext();
  const [revealed, setRevealed] = useState(false);
  if (!ctx) return null;
  const { explanations } = ctx;
  const explanation =
    target.kind === 'item'
      ? explanations.getItemExplanation(target.fieldKey, target.rowId)
      : explanations.getSectionExplanation(target.sectionId, target.title);
  const label = target.kind === 'section' ? t('explainPanel.explainSection') : t('explainPanel.explain');
  const explanationError = loadErrorText(explanations.error, t('explainPanel.loadError'));

  const handleToggle = () => {
    if (!revealed) explanations.ensureLoaded();
    setRevealed((r) => !r);
  };

  return (
    <div>
      <Button
        size="sm"
        variant="ghost"
        onClick={handleToggle}
        aria-expanded={revealed}
        data-testid={testId}
      >
        <Lightbulb className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
        {revealed ? t('explainPanel.hideExplanation') : label}
      </Button>
      {/* Live region: the explanation arrives asynchronously after the click, so
          assistive tech hears it land (same idiom as the editor's AssistPopover). */}
      {revealed && (
        <div
          aria-live="polite"
          className="mt-2 rounded-card border border-brand-slate-200 bg-brand-slate-50 p-3 text-sm"
          data-testid={`${testId}-panel`}
        >
          {explanation ? (
            <>
              <GeneratedLanguageNotice generatedLanguage={explanations.generatedLanguage} className="mb-2" />
              <p className="whitespace-pre-wrap text-brand-slate-700">{explanation}</p>
              {explanations.disclaimer && (
                <p className="mt-2 text-xs text-brand-slate-500">{explanations.disclaimer}</p>
              )}
            </>
          ) : explanations.isLoading ? (
            <span className="flex items-center gap-2 text-brand-slate-500">
              <Spinner size="sm" /> {t('explainPanel.explaining')}
            </span>
          ) : explanationError ? (
            <div role="alert">
              <Notice variant="error" title={explanationError} />
            </div>
          ) : (
            <p className="text-brand-slate-500">
              {target.kind === 'section' ? t('explainPanel.noExplanationSection') : t('explainPanel.noExplanationItem')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
